import { useMemo } from 'react'
import { Select } from '@/components/select'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import type { MatterSelectDropdownProps } from './types'
import type { MatterRead } from '@/matter/store/schemas'
import { SelectItem } from '../select/types'

/**
 * MatterSelectDropdown - Conversation Matter Assignment
 *
 * A component for assigning matters to conversations.
 * Built on the generic Select with matter-specific functionality.
 *
 * This component is specifically designed for conversation matter assignment flows, such as the MatterBoundInterception and ChatOverflowMenu components.
 *
 * ## Key Props
 * - `items`: Array of matters to display in the dropdown
 * - `onCreateNewMatter`: When provided, shows "New Matter" button and calls this when clicked
 *
 * @example
 * // Inline matter assignment
 * <MatterSelectDropdown
 *   items={items}
 *   selectedItemId={selectedItemId}
 *   onSelect={onSelect}
 * />
 */

export function MatterSelectDropdown({
  className,
  emptyStateMessage,
  fullWidthContent = false,
  items,
  onCreateNewMatter,
  onSelect,
  placeholder = 'Select Matter',
  selectedItemId,
  size,
  triggerLabel,
  variant = 'inline',
  ...props
}: MatterSelectDropdownProps) {
  // Map the list of matters to a list of select items
  const mapMatterToSelectItem = (matter: MatterRead): SelectItem => {
    if (isUnassignedMatter(matter)) {
      return { id: matter.id, label: matter.name, badge: 'VIEW ONLY' }
    }
    return { id: matter.id, label: matter.name }
  }

  const matterItems: SelectItem[] = useMemo(() => {
    return items.map(mapMatterToSelectItem)
  }, [items])

  const handleSelect = (itemId: string) => {
    onSelect(itemId)
  }

  return (
    <Select
      className={className}
      createAction={onCreateNewMatter ? { label: 'New Matter', onClick: onCreateNewMatter } : undefined}
      emptyStateMessage={!items?.length ? emptyStateMessage : undefined}
      fullWidthContent={fullWidthContent}
      items={matterItems}
      onSelect={handleSelect}
      placeholder={placeholder}
      selectedItemId={selectedItemId}
      size={size}
      triggerBadge={matterItems.find((item) => item.id === selectedItemId)?.badge}
      triggerLabel={triggerLabel}
      variant={variant}
      {...props}
    />
  )
}
